// Client for the conversation-document routes
// (`/accounts/:accountId/conversations[...]`). Used by the web chat composer
// to materialise conversations and upload file attachments before the first
// message is sent.
import { ConversationSchema, ConversationDocumentResponseSchema, } from '@packages/types';
import { api, getApiUrl, getApiBridge, parseErrorResponse, filenameFromResponse } from '../client.js';
/**
 * Materialise a web conversation for a given account.
 * Idempotent: calling with the same idempotencyKey returns the existing
 * conversation without creating a duplicate. Use before uploading
 * conversation documents when no active conversation exists.
 */
export function createConversation(accountId, { idempotencyKey, entryPoint }) {
    return api(`/accounts/${accountId}/conversations`, ConversationSchema, {
        method: 'POST',
        body: { idempotencyKey, entryPoint },
    });
}
/**
 * Upload a file attachment to a conversation.
 * Uses raw fetch with FormData – the api() wrapper is JSON-only.
 *
 * Re-sending a file that is already attached to the conversation (same filename
 * and byte-size) returns the attached document rather than creating a duplicate
 * row. The guard is keyed on live document state, not a stored reservation, so
 * it releases as soon as that document is deleted: deleting an attachment and
 * re-uploading the same file attaches it afresh rather than replaying a dangling
 * document id.
 */
export async function uploadConversationDocument(accountId, conversationId, file) {
    const bridge = getApiBridge();
    const baseUrl = getApiUrl();
    const token = bridge.getToken();
    const formData = new FormData();
    formData.append('file', file);
    const headers = {};
    if (token) {
        headers['Authorization'] = `Bearer ${token}`;
    }
    const response = await fetch(`${baseUrl}/accounts/${accountId}/conversations/${conversationId}/documents`, {
        method: 'POST',
        headers,
        body: formData,
    });
    if (response.status === 401) {
        bridge.removeToken();
        bridge.onUnauthorized();
        throw new Error('Session expired');
    }
    if (!response.ok) {
        throw await parseErrorResponse(response);
    }
    const json = await response.json();
    const result = ConversationDocumentResponseSchema.safeParse(json);
    if (!result.success) {
        throw new Error('Invalid response from server');
    }
    return result.data;
}
/**
 * Constructs the authenticated download URL for a conversation document.
 * This URL requires a Bearer token – it cannot be used as a plain <a href>.
 * Use downloadConversationDocument() to fetch with auth and open the file.
 */
export function getConversationDocumentDownloadUrl(accountId, conversationId, documentId) {
    return `${getApiUrl()}/accounts/${accountId}/conversations/${conversationId}/documents/${documentId}/download`;
}
/**
 * Fetches a conversation document as a Blob with auth headers and opens it.
 * Tries window.open first (opens in a new tab for PDFs/images), then falls
 * back to an <a download> click if the popup is blocked.
 */
export async function downloadConversationDocument(accountId, conversationId, documentId) {
    const bridge = getApiBridge();
    const token = bridge.getToken();
    if (!token) {
        bridge.onUnauthorized();
        throw new Error('Session expired');
    }
    const url = getConversationDocumentDownloadUrl(accountId, conversationId, documentId);
    const response = await fetch(url, {
        headers: { Authorization: `Bearer ${token}` },
    });
    if (response.status === 401) {
        bridge.removeToken();
        bridge.onUnauthorized();
        throw new Error('Session expired');
    }
    if (!response.ok) {
        throw new Error(`Download failed: ${response.statusText}`);
    }
    const blob = await response.blob();
    const blobUrl = URL.createObjectURL(blob);
    // Try opening in a new tab (best for PDF/image preview)
    const newTab = window.open(blobUrl, '_blank');
    if (!newTab) {
        // Popup blocked – fall back to <a download> click
        const filename = filenameFromResponse(response, 'document');
        const a = document.createElement('a');
        a.href = blobUrl;
        a.download = filename;
        a.click();
    }
    setTimeout(() => URL.revokeObjectURL(blobUrl), 60_000);
}
